import useClassNames from '@medflex/hooks/useClassNames';
import getDurationFormatted from '@medflex/utils/getDurationFormatted';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PlaybackButtonGroup from './PlaybackButtonGroup';
import PlaybackRateChangeButton from './PlaybackRateChangeButton';

const Player = ({
  className,
  hidden,
  src,
  caption,
  defer,
  initialDuration,
  children,
  ...props
}) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(initialDuration);
  const [srcUrl, setSrcUrl] = useState(null);
  const [generator] = useState(src);
  const [currentTime, setCurrentTime] = useState(0);
  const [state, setState] = useState('idle');

  const audioRef = useRef();

  const cn = useClassNames(
    () => [
      !hidden && 'flex',
      'flex-col',
      'gap-1',
      'items-start',
      'p-2',
      'pt-1',
      'transition',
      'rounded',
      'flex-grow',
      'flex-shrink-0',
      '@container',
      'h-full',
      className,
    ],
    [className],
  );

  const sliderCn = useClassNames(
    () => [
      'absolute',
      'z-10',
      '-top-1',
      'left-0',
      'appearance-none',
      'bg-transparent',
      'h-2',
      'w-full',
      'rounded-lg',
      'cursor-pointer',
      'focus:outline-none',
      '[&::-webkit-slider-thumb]:w-1.5',
      '[&::-webkit-slider-thumb]:h-4',
      '[&::-webkit-slider-thumb]:appearance-none',
      '[&::-webkit-slider-thumb]:bg-blue-500',
      '[&::-webkit-slider-thumb]:rounded',
      '[&::-moz-range-thumb]:w-1.5',
      '[&::-moz-range-thumb]:h-4',
      '[&::-moz-range-thumb]:appearance-none',
      '[&::-moz-range-thumb]:bg-blue-500',
      '[&::-moz-range-thumb]:rounded',
      '[&::-moz-range-thumb]:border-0',
    ],
    [],
  );

  const togglePlayback = () => {
    if (state === 'idle') {
      setState('loading');
      return;
    }

    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleSeeking = useCallback(
    e => {
      if (state === 'idle') {
        setState('loading');
      }
      audioRef.current.currentTime = e.target.value;
    },
    [state],
  );

  const next = useCallback(async () => {
    if (generator && typeof generator.next === 'function') {
      const { value: url, done } = await generator.next();
      if (!done) {
        setSrcUrl(url);
        return;
      }
    }

    setState('error');
  }, [generator]);

  const handleLoadedData = () => {
    setState('success');
    const audio = audioRef.current;
    if (audio.duration === Infinity) {
      audio.currentTime = 10000000 * Math.random();
    }
  };

  const handleError = () => {
    next();
  };

  const handlePlay = () => setPlaying(true);
  const handlePause = () => setPlaying(false);

  const updateCurrentTime = () => {
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleDuration = () => {
    if (audioRef.current.duration !== Infinity) {
      setDuration(audioRef.current.duration);
      audioRef.current.currentTime = 0;
    }
  };

  const progress = getDurationFormatted(currentTime > 0 && currentTime);

  useEffect(() => {
    if (defer) {
      if (state === 'loading') {
        if (generator && generator.next) {
          next();
          return;
        }
        setSrcUrl(generator);
      }
    } else if (state === 'idle') {
      setState('success');
      setSrcUrl(generator);
    }
  }, [defer, state, generator, next]);

  const handlePlaybackRateChange = useCallback(playbackRate => {
    audioRef.current.playbackRate = playbackRate;
  }, []);

  const skipForwardOrBackward = useCallback(seconds => {
    audioRef.current.currentTime += seconds;
  }, []);

  const renderProgressBar = () => (
    <div className="flex flex-row items-center w-full gap-3 text-blue-900 text-sm h-full">
      <span className="whitespace-nowrap">{progress}</span>
      <div className="relative w-full">
        <input
          type="range"
          value={Math.floor(currentTime)}
          min={0}
          max={Math.floor(duration)}
          onChange={handleSeeking}
          className={sliderCn}
        />
        <div className="w-full h-2 bg-blue-200 rounded-lg absolute -top-1 left-0" />
        <div
          className="h-2 bg-blue-500 rounded-l-lg absolute -top-1 left-0"
          style={{
            width: `${(Math.floor(currentTime) / Math.floor(duration)) * 100}%`,
          }}
        />
      </div>

      <span className="whitespace-nowrap">{getDurationFormatted(duration)}</span>
    </div>
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div data-component="VoiceMessage__Player" className={cn} {...props}>
      <div className="flex flex-row w-full">
        <p className="text-blue-900 text-sm font-semibold pb-1 grow">{caption}</p>
        {children}
      </div>
      <audio
        ref={audioRef}
        src={srcUrl}
        onError={handleError}
        onLoadedData={handleLoadedData}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={updateCurrentTime}
        onDurationChange={handleDuration}
        autoPlay={defer}
      >
        <track kind="captions" audio="Audio" />
      </audio>

      <div className="grid grid-rows-2 grid-cols-5 w-full items-center @md:flex @md:flex-row @md:flex-1 @md:gap-4">
        <div className="col-span-5 @md:order-3 @md:grow">{renderProgressBar()}</div>

        <div className="hidden w-px h-full bg-blue-900 @md:block @md:order-2" />

        <div className="col-span-3 col-start-2 @md:order-1">
          <PlaybackButtonGroup
            isLoading={state === 'loading'}
            hasError={state === 'error'}
            isPlaying={Boolean(playing)}
            onWinding={skipForwardOrBackward}
            onTogglePlayback={togglePlayback}
          />
        </div>

        <div className="col-span-1 justify-self-end @md:order-3">
          <PlaybackRateChangeButton onPlaybackRateChange={handlePlaybackRateChange} />
        </div>
      </div>
    </div>
  );
};

Player.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  hidden: PropTypes.bool,
  defer: PropTypes.bool,
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  caption: PropTypes.string,
  initialDuration: PropTypes.number,
  children: PropTypes.node,
};

Player.defaultProps = {
  className: '',
  hidden: false,
  src: null,
  caption: null,
  defer: false,
  initialDuration: null,
  children: null,
};

export default Player;
